import Input from "@/components/common/Input";
import { CropSettings } from "@/interfaces/crop-settings";
import { Plus } from "lucide-react";
import { Button } from "primereact/button";
import { Tag } from "primereact/tag";

const colors = [
  "rgba(59,130,246,0.55)",
  "rgba(234,179,8,0.55)",
  "rgba(16,185,129,0.55)",
  "rgba(239,68,68,0.55)",
  "rgba(147,51,234,0.55)",
  "rgba(251,113,133,0.55)",
  "rgba(14,165,233,0.55)",
  "rgba(245,158,11,0.55)",
  "rgba(34,197,94,0.55)",
  "rgba(220,38,38,0.55)",
];

interface Props {
  data: CropSettings;
}

const Name = ({ data }: Props) => {
  const value = data.name;
  return (
    <Input className="pointer-events-none border-0" value={value} readOnly />
  );
};

const Mode = ({ data }: Props) => {
  const value = data.mode;
  return (
    <Input
      className="pointer-events-none border-0 capitalize"
      value={value}
      readOnly
    />
  );
};

const ProductTypes = ({ data }: Props) => {
  return (
    <div className="flex flex-wrap gap-2">
      {data.productTypes
        .sort((a, b) => a._id.localeCompare(b._id))
        .map((t, i) => {
          return (
            <Tag
              className="truncate"
              key={t._id}
              value={t.name}
              style={{ backgroundColor: colors[i % colors.length] }}
            ></Tag>
          );
        })}
    </div>
  );
};

interface PreviewProps {
  data: CropSettings;
  imageUrl?: string;
  aspectRatio?: number;
  showLabels?: boolean;
}

export const Preview = ({ data, showLabels = false }: PreviewProps) => {
  return (
    <div className="relative flex h-20 w-20">
      <div className="relative flex h-full w-full">
        {data.regions.map((r, i) => (
          <div
            key={i}
            className="absolute transition-all duration-200"
            style={{
              left: `${r.leftRatio * 100}%`,
              top: `${r.topRatio * 100}%`,
              width: `${r.widthRatio * 100}%`,
              height: `${r.heightRatio * 100}%`,
              backgroundColor: colors[i % colors.length],
            }}
          >
            {showLabels && (
              <div className="absolute top-1 left-1 rounded bg-black/50 px-1.5 py-0.5 text-[10px] text-white">
                #{i + 1} — {(r.widthRatio * 100).toFixed(0)}% x{" "}
                {(r.heightRatio * 100).toFixed(0)}%
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
};

const Actions = ({
  data,
  onAdd,
}: Props & {
  onAdd?: (data: CropSettings) => void;
  onDetails?: (data: CropSettings) => void;
}) => {
  return (
    <div className="flex gap-2">
      <Button
        severity="info"
        outlined
        style={{ width: 28, height: 28 }}
        tooltip="Apply to ..."
        tooltipOptions={{ position: "top" }}
        icon={<Plus size={18} />}
        onClick={() => onAdd?.(data)}
      />
    </div>
  );
};

export { Name, Mode, ProductTypes, Actions };
